import * as THREE from 'three';
import type {EllipseIntersectionDefinition2D} from './EllipseCircleIntersectionSolver';
import {
    TrigonometricEquationRootSolver,
    type TrigonometricEquationCoefficients,
} from './TrigonometricEquationRootSolver';

/**
 * 两个二维椭圆的单个真实交点。
 */
export interface EllipseEllipseIntersection2D {
    /** 二维交点坐标。 */
    point: THREE.Vector2;
    /** 交点在第一个椭圆上的参数角，范围 `[0,2π)`。 */
    firstEllipseAngle: number;
    /** 交点在第二个椭圆上的参数角，范围 `[0,2π)`。 */
    secondEllipseAngle: number;
}

/**
 * 两个二维椭圆的求交结果。
 */
export type EllipseEllipseIntersectionResult2D = {
    /** `points` 表示有限交点；`coincident` 表示两条完整支撑椭圆重合。 */
    relation: 'points' | 'coincident';
    /** 全部唯一真实交点；重合时为空。 */
    intersections: EllipseEllipseIntersection2D[];
};

/**
 * 二维椭圆—椭圆真实交点求解器。
 *
 * 职责：
 * 1. 把第一个椭圆参数方程转换到第二个椭圆的单位圆标准坐标系；
 * 2. 构造二次三角方程并复用公共四次方程根求解；
 * 3. 回代双方真实椭圆方程，输出双方参数角并按空间位置去重；
 * 4. 把完全重合明确标记为 coincident，禁止制造有限裁剪边界。
 */
export class EllipseEllipseIntersectionSolver extends TrigonometricEquationRootSolver {
    /** 隐式方程回代残差容差。 */
    private static readonly IMPLICIT_RESIDUAL_TOLERANCE = 1e-9;

    /** 世界交点相对去重容差。 */
    private static readonly POINT_RELATIVE_TOLERANCE = 1e-10;

    /**
     * 求解两个完整支撑椭圆的关系和全部有限交点。
     * @param firstEllipse 第一个椭圆定义。
     * @param secondEllipse 第二个椭圆定义。
     * @returns 有限交点或完全重合关系。
     */
    public solve(
        firstEllipse: EllipseIntersectionDefinition2D,
        secondEllipse: EllipseIntersectionDefinition2D,
    ): EllipseEllipseIntersectionResult2D {
        if (!this.hasValidDefinition(firstEllipse) || !this.hasValidDefinition(secondEllipse)) {
            console.error('[EllipseEllipseIntersectionSolver] 椭圆定义无效，无法计算真实交点。', {
                firstEllipse,
                secondEllipse,
            });
            return {relation: 'points', intersections: []};
        }

        const firstCosine = Math.cos(firstEllipse.rotation);
        const firstSine = Math.sin(firstEllipse.rotation);
        const firstCosineBasis = new THREE.Vector2(
            firstEllipse.radiusX * firstCosine,
            firstEllipse.radiusX * firstSine,
        );
        const firstSineBasis = new THREE.Vector2(
            -firstEllipse.radiusY * firstSine,
            firstEllipse.radiusY * firstCosine,
        );

        // 将第一椭圆的中心和两条参数基向量映射到第二椭圆单位圆坐标系。
        const canonicalOrigin = this.transformPointToCanonical(secondEllipse, firstEllipse.center);
        const canonicalCosineBasis = this.transformVectorToCanonical(secondEllipse, firstCosineBasis);
        const canonicalSineBasis = this.transformVectorToCanonical(secondEllipse, firstSineBasis);
        const coefficients: TrigonometricEquationCoefficients = [
            canonicalCosineBasis.dot(canonicalCosineBasis),
            2 * canonicalCosineBasis.dot(canonicalSineBasis),
            canonicalSineBasis.dot(canonicalSineBasis),
            2 * canonicalOrigin.dot(canonicalCosineBasis),
            2 * canonicalOrigin.dot(canonicalSineBasis),
            canonicalOrigin.lengthSq() - 1,
        ];
        const roots = this.solveTrigonometricEquation(coefficients);
        if (roots.isIdentical) {
            return {relation: 'coincident', intersections: []};
        }

        const geometryScale = Math.max(
            firstEllipse.radiusX,
            firstEllipse.radiusY,
            secondEllipse.radiusX,
            secondEllipse.radiusY,
            firstEllipse.center.distanceTo(secondEllipse.center),
        );
        const pointToleranceSquared = Math.max(
            geometryScale * EllipseEllipseIntersectionSolver.POINT_RELATIVE_TOLERANCE,
            Number.EPSILON,
        ) ** 2;
        const intersections: EllipseEllipseIntersection2D[] = [];

        roots.angles.forEach((firstEllipseAngle) => {
            const point = firstEllipse.center.clone()
                .addScaledVector(firstCosineBasis, Math.cos(firstEllipseAngle))
                .addScaledVector(firstSineBasis, Math.sin(firstEllipseAngle));
            const secondCanonicalPoint = this.transformPointToCanonical(secondEllipse, point);
            const residual = Math.abs(secondCanonicalPoint.lengthSq() - 1);
            if (residual > EllipseEllipseIntersectionSolver.IMPLICIT_RESIDUAL_TOLERANCE) {
                return;
            }
            if (intersections.some((candidate) => candidate.point.distanceToSquared(point) <= pointToleranceSquared)) {
                return;
            }

            intersections.push({
                point,
                firstEllipseAngle,
                secondEllipseAngle: this.normalizeAngle(Math.atan2(
                    secondCanonicalPoint.y,
                    secondCanonicalPoint.x,
                )),
            });
        });

        return {relation: 'points', intersections};
    }

    /**
     * 判断椭圆定义是否有效。
     * @param ellipse 椭圆定义。
     * @returns 参数均有限且两个半轴为正时返回 true。
     */
    private hasValidDefinition(ellipse: EllipseIntersectionDefinition2D): boolean {
        return Number.isFinite(ellipse.center.x)
            && Number.isFinite(ellipse.center.y)
            && Number.isFinite(ellipse.radiusX)
            && ellipse.radiusX > 0
            && Number.isFinite(ellipse.radiusY)
            && ellipse.radiusY > 0
            && Number.isFinite(ellipse.rotation);
    }

    /**
     * 将世界二维点转换到指定椭圆单位圆坐标系。
     * @param ellipse 目标椭圆。
     * @param point 世界二维点。
     * @returns 单位圆标准坐标。
     */
    private transformPointToCanonical(
        ellipse: EllipseIntersectionDefinition2D,
        point: THREE.Vector2,
    ): THREE.Vector2 {
        return this.transformVectorToCanonical(ellipse, point.clone().sub(ellipse.center));
    }

    /**
     * 将世界二维向量转换到指定椭圆单位圆坐标系。
     * @param ellipse 目标椭圆。
     * @param vector 世界二维向量。
     * @returns 单位圆标准坐标向量。
     */
    private transformVectorToCanonical(
        ellipse: EllipseIntersectionDefinition2D,
        vector: THREE.Vector2,
    ): THREE.Vector2 {
        const cosine = Math.cos(ellipse.rotation);
        const sine = Math.sin(ellipse.rotation);
        return new THREE.Vector2(
            (cosine * vector.x + sine * vector.y) / ellipse.radiusX,
            (-sine * vector.x + cosine * vector.y) / ellipse.radiusY,
        );
    }
}
